<template>
    <div class="password-input sentry-mask">
        <div
            :class="[
                'password-input__input input',
                {
                    active: focused,
                    error: hasError,
                },
            ]"
        >
            <input
                id="password"
                ref="input"
                v-model="password"
                :type="visible ? 'text' : 'password'"
                autocomplete="off"
                @focus="onFocus"
                @blur="onBlur"
            />
            <div class="password-input__append">
                <svg-icon :name="visible ? 'visibility_off' : 'visibility'" @click="visible = !visible" />
            </div>
        </div>
        <div v-if="showHints && isDirty" class="password-input__hints">
            <div v-for="hint in passwordHints" :key="hint.key" class="hint">
                <svg-icon :name="hint.validation ? 'check' : 'close'" />
                <span class="hint__value">
                    {{ $t(`form_inputs.${hint.key}`) }}
                    <span v-if="hint.key === 'password_strength'" :class="hint.value">
                        {{ $t(`form_inputs.password_strength_${hint.value}`) }}
                    </span>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PasswordInput',
    props: {
        value: {
            type: String,
            default: null,
        },
        showHints: {
            type: Boolean,
            default: false,
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        isDirty: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            visible: false,
            focused: false,
        };
    },
    computed: {
        password: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        passwordHints() {
            const { validation, value } = this.passwordStrengthValidation;

            return [
                { key: 'password_strength', validation, value },
                { key: 'password_hint_length', validation: this.lengthValidation },
                { key: 'password_hint_capital_letter', validation: this.upperCaseValidation },
                { key: 'password_hint_number', validation: this.numberValidation },
            ];
        },
        passwordStrengthValidation() {
            const atLeastEightCharacter = '(?=.{8,})';
            const atLeastOneUpperCase = '(?=.*[A-Z])';
            const atLeastOneLowerCase = '(?=.*[a-z])';
            const atLeastOneDigit = '(?=.*[0-9])';
            const atLeastOneSpecialCharacter = '(?=.*[^A-Za-z0-9])';
            const mediumPassword = new RegExp(
                `${atLeastEightCharacter}${atLeastOneUpperCase}${atLeastOneLowerCase}${atLeastOneDigit}`
            );
            const strongPassword = new RegExp(
                `${atLeastEightCharacter}${atLeastOneUpperCase}${atLeastOneLowerCase}${atLeastOneDigit}${atLeastOneSpecialCharacter}`
            );

            let value = 'weak';
            let validation = false;

            if (strongPassword.test(this.password)) {
                value = 'strong';
                validation = true;
            } else if (mediumPassword.test(this.password)) {
                value = 'medium';
                validation = true;
            }

            return { validation, value };
        },
        lengthValidation() {
            return this.password && this.password.length >= 8;
        },
        upperCaseValidation() {
            return /(?=.*[A-Z])/.test(this.password);
        },
        numberValidation() {
            return /[0-9]/.test(this.password);
        },
    },
    methods: {
        onBlur() {
            this.focused = false;
        },
        onFocus() {
            this.focused = true;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.password-input {
    display: flex;
    flex-direction: column;

    input {
        padding: 0;
        border: none;
        outline: none;
        @include font-size(16px);
        line-height: normal;
        width: 100%;
        font-family: inherit;

        &:focus {
            border: none;
            outline: none;
        }
    }

    &__input {
        display: flex;
        align-items: center;
        position: relative;

        &.active {
            border-color: $border-color-active;
        }

        &.error {
            border-color: $warning-darkest;
            color: $warning-darkest;
        }
    }

    &__append {
        cursor: pointer;
        display: flex;
        align-items: center;
        padding-left: 12px;

        .svg-icon {
            height: 24px;
            width: 24px;
        }
    }

    &__hints {
        margin-top: 8px;
        @include font-size(14px);
        line-height: 20px;

        .hint {
            display: flex;
            align-items: center;

            .svg-icon {
                width: 20px;
                height: 20px;
                margin-right: 4px;
            }

            &__value {
                .weak {
                    color: $warning-darkest;
                }
            }
        }
    }
}
</style>
